import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";

import { ITypeaheadOptions } from "../../../interfaces/i-typeahead";
import { ReferralService } from "../../../services/referral-service";
import { IPageCallbackResult } from "../../../resources-vue/vue-interfaces/i-page-container";
import { PAGE_FAIL_RESPONSE } from "../../../resources/elements/pagination";
import { IExternalReferralsResponse } from "../../../interfaces/i-get-referral";
import config from "../../../common/config";
import { ApplicationEnum } from "../../../enums/ApplicationEnum";
import { helperTestingAgency } from "../../../common/vue-helpers/testing-agency-check";

import MultiSelectFilter from "../../../resources-vue/vue-elements/MultiSelectFilter/MultiSelectFilter.vue";
import Pagination from "../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import ErrorMessageHandler from "../../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import CreateExternalReferralRow from "./CreateExternalReferralRow.vue";
import SortableTh from "../../../resources-vue/vue-custom-elements/SortableTh/SortableTh.vue";
import ListLayout from "../../../resources-vue/vue-custom-elements/ListLayout/ListLayout.vue";
import StaticTableFormater from "../../../resources-vue/vue-custom-elements/StaticTableFormater/StaticTableFormater.vue";

export default defineComponent({
    components: {
        ListLayout,
        MultiSelectFilter,
        Pagination,
        SortableTh,
        ErrorMessageHandler,
        CreateExternalReferralRow,
        StaticTableFormater
    },

    data(vm) {
        return {
            _referralService: null,
            term: "" as string,
            dynamicTableToggled: false,
            isLoading: false as boolean,
            agencySelectOptions: [] as ITypeaheadOptions[],
            branchResponse: null,
            selectedBranches: [] as any[],
            selectedPatientStatus: true,
            patientStatusEnums: [
                {
                    label: "Pending",
                    value: true
                },
                {
                    label: "Deleted",
                    value: false
                }
            ],
            externalReferralListPagination: null,
            responseHandler: {
                data: null,
                stamp: null,
                requests: []
            },
            isError: false as boolean,
            sortOrder: "asc",
            sortBy: "dateOfBirth",
            page: null,
            columns: 9,
            agencyService: [
                {
                    name: "Hospice",
                    show: this.showNewReferralHospice,
                    id: ApplicationEnum.AxxessHospiceFE,
                    isNonMedical: false
                },
                {
                    name: "Palliative",
                    show: this.showNewReferralPalliative,
                    id: ApplicationEnum.AxxessPalliative,
                    isNonMedical: false
                },
                {
                    name: "Home Health",
                    show: this.showNewReferralHomeHealth,
                    id: ApplicationEnum.AgencyCore,
                    isNonMedical: false
                },
                {
                    name: "Home Care (Medical)",
                    show: this.showNewReferralHomeCare,
                    id: ApplicationEnum.HomeCare,
                    isNonMedical: false
                },
                {
                    name: "Home Care (Non-Medical)",
                    show: this.showNewReferralHomeCare,
                    id: ApplicationEnum.HomeCare,
                    isNonMedical: true
                }
            ],
            pageSize: 10
        };
    },

    created() {
        this._referralService = Container.instance.get(ReferralService);
        this.externalReferralListPagination = this.$refs.externalReferralListPagination as typeof Pagination;
    },

    async mounted() {
        const showExternalReferral =
            (await helperTestingAgency.checkHasTestingAgency()) ||
            (await helperTestingAgency.checkHasExternalReferralTestingAgencies());
        if (!showExternalReferral) this.$router.push("/clinician");
        this.branchResponse = await this._referralService.getExternalReferralBranches();
        this.externalReferralListPagination = this.$refs.externalReferralListPagination as typeof Pagination;
        this.pageReset();
    },

    watch: {
        selectedBranches: {
            handler() {
                this.pageReset();
            },
            deep: true
        },
        selectedPatientStatus() {
            this.pageReset();
        }
    },

    computed: {
        hasData() {
            return this.page?.itemCount > 0;
        },

        loadingCount() {
            return this.page?.items?.length > 0 ? this.page.items.length : config.pageSize;
        }
    },

    methods: {
        search() {
            if (!this.isLoading) {
                this.pageReset();
            }
        },

        async agencySelectOptionsFetch(filter: string): Promise<ITypeaheadOptions[]> {
            this.agencySelectOptions = this.branchResponse
                ?.map((branch: string, index: number) => ({
                    name: branch,
                    value: index
                }))
                .filter((x: any) => !filter || x.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()));
            return this.agencySelectOptions || [];
        },

        updateBranches(branches: any[]) {
            this.selectedBranches=[...branches];
        },

        pageReset() {
            this.externalReferralListPagination.reset();
        },

        async pageChangedCallback(pageNumber: number, pageSize: number): Promise<IPageCallbackResult> {
            try {
                let callTime = Date.now();
                this.responseHandler.stamp = callTime;
                this.isLoading = true;
                this.responseHandler.data = null;
                this.isError = false;
                let payload = {
                    page: pageNumber,
                    pageLength: pageSize,
                    order: this.sortOrder,
                    sortBy: this.sortBy,
                    term: this.term,
                    isActive: this.selectedPatientStatus,
                    agencyBranches: this.selectedBranches.map((branch: any) => branch.name)
                };
                const response: IExternalReferralsResponse = await this._referralService.getExternalReferrals(payload);
                this.responseHandler.requests.push(response);
                let data = await response;
                if (this.responseHandler.stamp == callTime) {
                    this.responseHandler.data = data;
                } else {
                    data =
                        this.responseHandler.data ||
                        (await this.responseHandler.requests[this.responseHandler.requests.length - 1]);
                }
                this.page = data;
                return {
                    totalPageCount: data.pageCount,
                    success: data.itemCount > 0,
                    totalCount: data.itemCount
                };
            } catch (e) {
                this.isError = true;
                return PAGE_FAIL_RESPONSE;
            } finally {
                this.isLoading = false;
            }
        },
        sortByUpdated(value: string) {
            if (value != this.sortBy) {
                this.sortBy = value;
                this.pageReset();
            }
        },
        sortOrderUpdated(value: string) {
            if (value != this.sortOrder) {
                this.sortOrder = value;
                this.pageReset();
            }
        },
        updatePageSizeChanged(value: number) {
            this.pageSize = value;
        },
        getCustomSettings() {
            return [
                { Width: 120, RowIndex: 1 },
                { Width: 128, RowIndex: 2 },
                { Width: 100, RowIndex: 8 }
            ];
        }
    }
});
